/**
 * BRR (Bit Rate Reduction) Decoder
 *
 * Decodes SNES BRR-encoded audio samples to 16-bit PCM.
 * BRR is a 4-bit ADPCM format used by the S-DSP.
 *
 * Format:
 * - 9 bytes per block = 16 samples
 * - Byte 0: Header (range, filter, loop flag, end flag)
 * - Bytes 1-8: 4-bit nibbles (2 samples per byte)
 *
 * Reference: https://wiki.superfamicom.org/brr-samples
 * Implementation matches split700 reference decoder.
 */

export interface BrrSample {
  /** Decoded 16-bit PCM samples */
  samples: Int16Array;
  /** Sample index where loop starts, or -1 if no loop */
  loopStart: number;
  /** Whether the sample has an end flag */
  hasEnd: boolean;
}

export interface BrrDecodeOptions {
  /** Stop decoding at end flag (default: true) */
  stopAtEnd?: boolean;
  /** Maximum blocks to decode (safety limit, default: 4096) */
  maxBlocks?: number;
}

/**
 * Sign-extend from 15 bits to 32 bits (matches S-DSP sclip15)
 */
function sclip15(x: number): number {
  return x & 0x4000 ? x | ~0x3fff : x & 0x3fff;
}

/**
 * Clamp to 16-bit signed range
 */
function sclamp16(x: number): number {
  return x > 32767 ? 32767 : x < -32768 ? -32768 : x;
}

/**
 * Decode BRR data to 16-bit PCM samples
 *
 * @param data Raw BRR data (must be multiple of 9 bytes)
 * @param options Decode options
 * @returns Decoded sample data
 */
export function decodeBrr(data: Uint8Array, options: BrrDecodeOptions = {}): BrrSample {
  const { stopAtEnd = true, maxBlocks = 4096 } = options;

  if (data.length < 9) {
    return { samples: new Int16Array(0), loopStart: -1, hasEnd: false };
  }

  const samples: number[] = [];
  let s1 = 0; // Previous sample (for filter) - 15-bit
  let s2 = 0; // Sample before previous (for filter) - 15-bit
  let loopStart = -1;
  let hasEnd = false;

  const numBlocks = Math.min(Math.floor(data.length / 9), maxBlocks);

  for (let blockIdx = 0; blockIdx < numBlocks; blockIdx++) {
    const blockOffset = blockIdx * 9;
    const header = data[blockOffset];

    // Parse header byte
    const endFlag = (header & 0x01) !== 0;
    const loopFlag = (header & 0x02) !== 0;
    const filter = (header >> 2) & 0x03;
    const range = (header >> 4) & 0x0f;
    const validRange = range <= 12;

    // Record loop point (the block with loop flag is the loop destination)
    if (loopFlag && loopStart === -1) {
      loopStart = samples.length;
    }

    // Decode 8 bytes → 16 samples (2 nibbles per byte)
    for (let byteIdx = 0; byteIdx < 8; byteIdx++) {
      const byte = data[blockOffset + 1 + byteIdx];

      // Extract and sign-extend nibbles (high nibble first, then low)
      // Sign extension: values 8-15 become -8 to -1
      const hi = byte >> 4;
      const lo = byte & 0x0f;
      const sample1 = hi >= 8 ? hi - 16 : hi;
      const sample2 = lo >= 8 ? lo - 16 : lo;

      for (let nybble = 0; nybble < 2; nybble++) {
        let out = nybble === 0 ? sample1 : sample2;

        // Apply range shift
        // For valid range: (out << range) >> 1
        // For invalid range (>12): out & ~0x7FF (zeros lower 11 bits)
        if (validRange) {
          out = (out << range) >> 1;
        } else {
          out = out & ~0x7ff;
        }

        // Apply filter using previous samples
        // These formulas match split700 exactly
        switch (filter) {
          case 0:
            // Direct - no filter
            break;
          case 1:
            // 15/16: out += S1 + ((-S1) >> 4)
            out += s1 + (-s1 >> 4);
            break;
          case 2:
            // 61/32 - 15/16: out += (S1 << 1) + ((-((S1 << 1) + S1)) >> 5) - S2 + (S2 >> 4)
            out += (s1 << 1) + (-(s1 * 2 + s1) >> 5) - s2 + (s2 >> 4);
            break;
          case 3:
            // 115/64 - 13/16: out += (S1 << 1) + ((-(S1 + (S1 << 2) + (S1 << 3))) >> 6) - S2 + (((S2 << 1) + S2) >> 4)
            out += (s1 << 1) + (-(s1 + (s1 << 2) + (s1 << 3)) >> 6) - s2 + ((s2 * 3) >> 4);
            break;
        }

        // Clamp to 16-bit, then sign-extend from 15-bit (matches S-DSP)
        out = sclip15(sclamp16(out));

        // Update filter state (15-bit values)
        s2 = s1;
        s1 = out;

        // Output as 16-bit (shift left by 1)
        samples.push(out << 1);
      }
    }

    // Check end flag
    if (endFlag) {
      hasEnd = true;
      if (stopAtEnd) break;
    }
  }

  return {
    samples: new Int16Array(samples),
    loopStart,
    hasEnd,
  };
}
